import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { ChevronLeft, ChevronRight, CalendarDays, Sparkles, PartyPopper, Cake } from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { Card } from '@/components/Card';
import Loader from '@/components/Loader';
import { holidayService, userService } from '@/services';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const TYPE_STYLES = {
  public: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300',
  festival: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  optional: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300',
  company: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300',
};

const isWeekend = (d) => d.day() === 0 || d.day() === 6;

export default function Holidays() {
  const [cursor, setCursor] = useState(dayjs().startOf('month'));
  const year = cursor.year();

  const { data, isLoading } = useQuery({
    queryKey: ['holidays', year],
    queryFn: () => holidayService.list({ year }),
  });

  const { data: bdayData } = useQuery({
    queryKey: ['birthdays'],
    queryFn: () => userService.birthdays(),
  });

  // Index holidays by "YYYY-MM-DD" for O(1) cell lookups.
  const byDate = useMemo(() => {
    const map = {};
    for (const h of data?.items || []) map[h.date] = h;
    return map;
  }, [data]);

  // Birthdays recur yearly, so index by "MM-DD". Each key holds everyone born that day.
  const bdayByKey = useMemo(() => {
    const map = {};
    for (const u of bdayData?.items || []) {
      const key = dayjs(u.dob).format('MM-DD');
      (map[key] = map[key] || []).push(u);
    }
    return map;
  }, [bdayData]);

  // Build a 6x7 grid starting on the Sunday on/before the 1st.
  const cells = useMemo(() => {
    const start = cursor.startOf('month').startOf('week');
    return Array.from({ length: 42 }, (_, i) => start.add(i, 'day'));
  }, [cursor]);

  const upcoming = useMemo(() => {
    const today = dayjs().format('YYYY-MM-DD');
    return (data?.items || [])
      .filter((h) => h.date >= today)
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [data]);

  // People whose birthday falls in the month currently on screen, sorted by day.
  const monthBirthdays = useMemo(() => {
    const mm = cursor.format('MM');
    return (bdayData?.items || [])
      .filter((u) => dayjs(u.dob).format('MM') === mm)
      .sort((a, b) => dayjs(a.dob).date() - dayjs(b.dob).date());
  }, [bdayData, cursor]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Holiday Calendar"
        subtitle="Weekends are off. Company & public holidays are highlighted below."
      />

      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <button className="btn-ghost p-1.5" onClick={() => setCursor((c) => c.subtract(1, 'month'))}>
              <ChevronLeft size={18} />
            </button>
            <div className="font-semibold text-lg flex items-center gap-2">
              <CalendarDays size={18} /> {cursor.format('MMMM YYYY')}
            </div>
            <button className="btn-ghost p-1.5" onClick={() => setCursor((c) => c.add(1, 'month'))}>
              <ChevronRight size={18} />
            </button>
          </div>

          {isLoading ? (
            <Loader />
          ) : (
            <div className="grid grid-cols-7 gap-1">
              {WEEKDAYS.map((w) => (
                <div
                  key={w}
                  className={`text-center text-xs font-semibold py-1.5 ${
                    w === 'Sun' || w === 'Sat' ? 'text-rose-500' : 'text-slate-500'
                  }`}
                >
                  {w}
                </div>
              ))}
              {cells.map((d) => {
                const key = d.format('YYYY-MM-DD');
                const inMonth = d.month() === cursor.month();
                const holiday = byDate[key];
                const bdays = bdayByKey[d.format('MM-DD')] || [];
                const weekend = isWeekend(d);
                const isToday = key === dayjs().format('YYYY-MM-DD');
                const bdayTitle = bdays.length ? `🎂 ${bdays.map((u) => u.name).join(', ')}` : '';
                return (
                  <div
                    key={key}
                    title={[holiday?.title, weekend && !holiday ? 'Weekend off' : '', bdayTitle].filter(Boolean).join(' · ')}
                    className={[
                      'min-h-[68px] rounded-lg border p-1.5 flex flex-col gap-1 transition',
                      inMonth ? '' : 'opacity-40',
                      holiday
                        ? 'border-rose-200 bg-rose-50/70 dark:border-rose-500/30 dark:bg-rose-500/10'
                        : weekend
                        ? 'border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800/40'
                        : 'border-slate-200 dark:border-slate-700',
                      isToday ? 'ring-2 ring-brand-500' : '',
                    ].join(' ')}
                  >
                    <div className="flex items-center justify-between">
                      <span className={`text-xs font-semibold ${weekend ? 'text-rose-500' : ''}`}>
                        {d.date()}
                      </span>
                      {weekend && !holiday && (
                        <span className="text-[9px] uppercase text-slate-400">Off</span>
                      )}
                    </div>
                    {holiday && (
                      <span className="text-[10px] leading-tight font-medium text-rose-600 dark:text-rose-300 line-clamp-2">
                        {holiday.title}
                      </span>
                    )}
                    {bdays.length > 0 && (
                      <span className="text-[10px] leading-tight font-medium text-pink-600 dark:text-pink-300 flex items-center gap-0.5 line-clamp-2">
                        <Cake size={10} className="shrink-0" />
                        {bdays.length === 1 ? bdays[0].name.split(' ')[0] : `${bdays.length} birthdays`}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-3 mt-4 text-xs text-slate-500">
            <span className="inline-flex items-center gap-1">
              <span className="w-3 h-3 rounded bg-rose-100 dark:bg-rose-500/20 border border-rose-300" /> Holiday
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="w-3 h-3 rounded bg-slate-100 dark:bg-slate-800 border border-slate-300" /> Weekend off
            </span>
            <span className="inline-flex items-center gap-1">
              <Cake size={12} className="text-pink-500" /> Birthday
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="w-3 h-3 rounded ring-2 ring-brand-500" /> Today
            </span>
          </div>
        </Card>

        <Card>
          <div className="font-semibold mb-3 flex items-center gap-2">
            <PartyPopper size={18} /> Upcoming holidays
          </div>
          {isLoading ? (
            <Loader />
          ) : upcoming.length === 0 ? (
            <div className="text-sm text-slate-500 py-4">No upcoming holidays this year.</div>
          ) : (
            <ul className="space-y-2">
              {upcoming.map((h) => (
                <li key={h._id} className="flex items-start gap-3">
                  <div className="shrink-0 w-12 text-center rounded-lg bg-slate-100 dark:bg-slate-800 py-1">
                    <div className="text-[10px] uppercase text-slate-500">{dayjs(h.date).format('MMM')}</div>
                    <div className="text-lg font-bold leading-none">{dayjs(h.date).format('DD')}</div>
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{h.title}</div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded capitalize ${TYPE_STYLES[h.type] || TYPE_STYLES.public}`}>
                        {h.type}
                      </span>
                      <span className="text-xs text-slate-400">{dayjs(h.date).format('dddd')}</span>
                    </div>
                    {h.description && <div className="text-xs text-slate-500 mt-0.5">{h.description}</div>}
                  </div>
                </li>
              ))}
            </ul>
          )}

          <div className="mt-4 pt-3 border-t border-slate-200 dark:border-slate-800 text-xs text-slate-500 flex items-start gap-2">
            <Sparkles size={14} className="shrink-0 mt-0.5 text-brand-500" />
            <span>Planning a leave around a holiday? Apply early — long weekends fill up fast.</span>
          </div>
        </Card>

        <Card>
          <div className="font-semibold mb-3 flex items-center gap-2">
            <Cake size={18} className="text-pink-500" /> Birthdays in {cursor.format('MMMM')}
          </div>
          {monthBirthdays.length === 0 ? (
            <div className="text-sm text-slate-500 py-4">No birthdays this month.</div>
          ) : (
            <ul className="space-y-2">
              {monthBirthdays.map((u) => {
                const isTodayBday = dayjs(u.dob).format('MM-DD') === dayjs().format('MM-DD');
                return (
                  <li key={u._id} className="flex items-center gap-3">
                    <div className="shrink-0 w-12 text-center rounded-lg bg-pink-50 dark:bg-pink-500/10 py-1">
                      <div className="text-[10px] uppercase text-pink-500">{dayjs(u.dob).format('MMM')}</div>
                      <div className="text-lg font-bold leading-none text-pink-600 dark:text-pink-300">{dayjs(u.dob).format('DD')}</div>
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate flex items-center gap-1.5">
                        {u.name}
                        {isTodayBday && <span className="text-[10px] px-1.5 py-0.5 rounded bg-pink-100 text-pink-700 dark:bg-pink-500/20 dark:text-pink-300 font-semibold">Today 🎉</span>}
                      </div>
                      {u.department && <div className="text-xs text-slate-400 truncate">{u.department}</div>}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}
